import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import { WorkflowNode } from '../../types/workflow.types';
import { Agent, AgentService } from '../../services/api/agent';

interface NodeConfigDialogProps {
  open: boolean;
  node: WorkflowNode | null;
  onClose: () => void;
  onSave: (node: WorkflowNode) => void;
}

const NodeConfigDialog: React.FC<NodeConfigDialogProps> = ({
  open,
  node,
  onClose,
  onSave,
}) => {
  const [config, setConfig] = useState<any>({});
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const agentService = useMemo(() => new AgentService(), []);

  useEffect(() => {
    if (node) {
      setConfig(node.data.config || {});
      setTitle(node.data.title || '');
      setDescription(node.data.description || '');
    }
  }, [node]);

  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const fetchedAgents = await agentService.getAgents();
      setAgents(fetchedAgents);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  }, [agentService]);

  // Fetch agents when dialog opens for teammate nodes
  useEffect(() => {
    if (open && node?.type === 'teammate' && agents.length === 0) {
      fetchAgents();
    }
  }, [open, node?.type, agents.length, fetchAgents]);

  const handleSave = () => {
    if (!node) return;

    const updatedNode: WorkflowNode = {
      ...node,
      data: {
        ...node.data,
        title,
        description,
        config,
      },
    };

    onSave(updatedNode);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const renderConfigFields = () => {
    if (!node) return null;

    switch (node.type) {
      case 'teammate':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>AI Teammate</InputLabel>
              <Select
                value={config.teammateId || ''}
                onChange={(e) => setConfig({ ...config, teammateId: e.target.value })}
                label="AI Teammate"
                disabled={loadingAgents}
              >
                {loadingAgents ? (
                  <MenuItem disabled>Loading agents...</MenuItem>
                ) : (
                  agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.custom_name || agent.name} ({agent.type})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Additional Instructions"
              multiline
              rows={3}
              value={config.prompt || ''}
              onChange={(e) => setConfig({ ...config, prompt: e.target.value })}
              placeholder="Additional context or instructions that will be appended to the teammate's default prompt"
              helperText="This will be added to the teammate's existing capabilities and instructions"
            />
          </>
        );

      case 'human':
        return (
          <>
            <TextField
              fullWidth
              margin="normal"
              label="Assigned User"
              value={config.assignedUser || ''}
              onChange={(e) => setConfig({ ...config, assignedUser: e.target.value })}
              placeholder="Email or username of assigned user"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Instructions"
              multiline
              rows={3}
              value={config.instructions || ''}
              onChange={(e) => setConfig({ ...config, instructions: e.target.value })}
              placeholder="Instructions for the human task"
            />
          </>
        );

      case 'api':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={config.method || 'GET'}
                onChange={(e) => setConfig({ ...config, method: e.target.value })}
                label="HTTP Method"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="API Endpoint URL"
              value={config.url || ''}
              onChange={(e) => setConfig({ ...config, url: e.target.value })}
              placeholder="https://api.example.com/endpoint"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Headers (JSON)"
              multiline
              rows={2}
              value={config.headers || ''}
              onChange={(e) => setConfig({ ...config, headers: e.target.value })}
              placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
            />
          </>
        );

      case 'condition':
        return (
          <>
            <TextField
              fullWidth
              margin="normal"
              label="Condition Expression"
              value={config.condition || ''}
              onChange={(e) => setConfig({ ...config, condition: e.target.value })}
              placeholder="variable === 'value' || status === 'complete'"
            />
            <TextField
              fullWidth
              margin="normal"
              label="True Branch Label"
              value={config.trueBranchLabel || 'Yes'}
              onChange={(e) => setConfig({ ...config, trueBranchLabel: e.target.value })}
            />
            <TextField
              fullWidth
              margin="normal"
              label="False Branch Label"
              value={config.falseBranchLabel || 'No'}
              onChange={(e) => setConfig({ ...config, falseBranchLabel: e.target.value })}
            />
          </>
        );

      case 'loop':
        return (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>Loop Type</InputLabel>
              <Select
                value={config.loopType || 'for'}
                onChange={(e) => setConfig({ ...config, loopType: e.target.value })}
                label="Loop Type"
              >
                <MenuItem value="for">For Loop</MenuItem>
                <MenuItem value="while">While Loop</MenuItem>
                <MenuItem value="forEach">For Each</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Loop Condition/Array"
              value={config.loopCondition || ''}
              onChange={(e) => setConfig({ ...config, loopCondition: e.target.value })}
              placeholder="items.length or variable < 10"
            />
          </>
        );

      default:
        return (
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            No specific configuration available for this node type.
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Configure {node?.type === 'teammate' ? 'AI Teammate' : node?.type || 'Node'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Node Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter a descriptive title"
        />
        <TextField
          fullWidth
          margin="normal"
          label="Description"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description of what this node does"
        />

        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle2" gutterBottom>
            Node Configuration
          </Typography>
          {renderConfigFields()}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeConfigDialog;
